import { Fragment, ReactNode, useContext, useEffect, useState } from "react";
import { ChevronDown, ChevronLeft, ChevronRight, Lightbulb, PanelLeftClose, PanelLeftOpen, X } from "lucide-react";
import { useLayout } from "@/contexts/LayoutContext";
import { MenuItem } from "@/types/domain";
import { ScrollArea } from "@/components/shadcn/scroll-area";
import { SearchBar } from "../SearchBar";
import { MenuItemRow } from "./MenuItemRow";
import { JumpToPalette } from "./JumpToPalette";
import { NavPanelContext, useNavPanelState } from "./NavPanelContext";
import { collapsedSectionsKey, getSectionName, getSelectedSections, useCollapsedSections } from "./collapsedSections";
import { navTipKey, useNavTipSnooze } from "./navTipSnooze";
import { RecentCompanies } from "./RecentCompanies";
import { useWhatsNew } from "../whats-new/WhatsNewContext";
import { HeaderTip, NAV_TIP_DELAY } from "../HeaderTip";


const SKELETON_COUNTS = [8, 3, 5, 4, 6, 7, 9, 4, 5, 3, 7, 6];

const isMac = () => /Mac|iPhone|iPad/.test(navigator.platform);

const SectionHeading = ({ label, isMini, isFirst, afterUngrouped, afterPinned, expanded, locked, onToggle }: { label: string; isMini: boolean; isFirst: boolean; afterUngrouped: boolean; afterPinned: boolean; expanded: boolean; locked: boolean; onToggle: () => void }) => {
  if (isMini) return isFirst ? null : <li role="separator" className="mx-2 my-1.5 border-t border-white/25" />;
  const sectionLabel = locked ? `${label} (contains the current page)` : `${expanded ? "Collapse" : "Expand"} ${label}`;
  return (
    <li role="presentation" className={isFirst ? "pr-1.5 pb-0.5 pt-1" : afterPinned ? "pr-1.5 pb-0.5 pt-3.5 [@media(max-height:1000px)]:pt-3" : afterUngrouped ? "mt-2 border-t border-white/15 pr-1.5 pb-0.5 pt-3.5 [@media(max-height:1000px)]:pt-3" : "pr-1.5 pb-0.5 pt-4 [@media(max-height:1000px)]:pt-3"}>
      <HeaderTip label={sectionLabel} side="right" delayDuration={NAV_TIP_DELAY}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-disabled={locked || undefined}
        data-testid={`nav-section-${label}`}
        className="flex w-full items-center gap-3.5 rounded py-1 pl-4 pr-2 text-left text-sm font-normal text-white/60 hover:bg-white/10 hover:text-white focus-visible:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/70"
      >
        {expanded
          ? <ChevronDown className="relative left-0.5 size-4 shrink-0" aria-hidden />
          : <ChevronRight className="relative left-0.5 size-4 shrink-0" aria-hidden />}
        <span className="flex-1 truncate">{label}</span>
      </button>
      </HeaderTip>
    </li>
  );
}

const GroupGap = ({ isMini }: { isMini: boolean }) =>
  isMini
    ? <li role="separator" className="mx-2 my-1.5 border-t border-white/25" />
    : <li role="presentation" aria-hidden className="mb-0! pt-4 [@media(max-height:1000px)]:pt-3" />;

type NavTipItem = { key: "jump" | "whatsnew"; testId: string; body: ReactNode; onTry: () => void }

const NavTip = ({ onTry, onDismiss, onTryWhatsNew, showWhatsNew, whatsNewFirst }: { onTry: () => void; onDismiss: () => void; onTryWhatsNew: () => void; showWhatsNew: boolean; whatsNewFirst: boolean }) => {
  const jump: NavTipItem = {
    key: "jump",
    testId: "nav-tip-action",
    onTry,
    body: <>Press <kbd className="rounded bg-white/15 px-1.5 text-[10px] font-medium">{isMac() ? "Cmd" : "Ctrl"} K</kbd> to jump to any page or setting.</>,
  };
  const whatsNew: NavTipItem = {
    key: "whatsnew",
    testId: "nav-tip-action-whatsnew",
    onTry: onTryWhatsNew,
    body: <>See what&apos;s new in Klipboard CRM. You can open it again any time from your user menu.</>,
  };
  const tips = !showWhatsNew ? [jump] : whatsNewFirst ? [whatsNew, jump] : [jump, whatsNew];
  const [index, setIndex] = useState(0);
  const step = (delta: number) => setIndex((i) => (i + delta + tips.length) % tips.length);

  return (
  <div data-testid="nav-tip" className="relative mx-2.5 mt-2.5 mb-1 shrink-0 [@media(max-height:640px)]:hidden rounded-lg bg-white/10 p-3 text-white/85 [@media(max-height:1000px)]:p-2">
    <HeaderTip label="Dismiss tip" side="top">
    <button
      type="button"
      onClick={onDismiss}
      aria-label="Dismiss tip"
      data-testid="nav-tip-dismiss"
      className="absolute right-1.5 top-1.5 flex size-5 items-center justify-center rounded text-white/60 hover:bg-white/15 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/60"
    >
      <X className="size-3.5" aria-hidden />
    </button>
    </HeaderTip>
    <div className="flex items-center gap-2 pr-7 text-[13px] font-semibold text-white whitespace-nowrap">
      <Lightbulb className="size-4 shrink-0" aria-hidden />
      <span>Tips</span>
      {tips.length > 1 && (
        <span className="ml-auto flex items-center gap-0.5 text-[11px] font-normal text-white/70">
          <button type="button" onClick={() => step(-1)} aria-label="Previous tip" data-testid="nav-tip-prev" className="flex size-5 items-center justify-center rounded hover:bg-white/15 hover:text-white">
            <ChevronLeft className="size-3.5" aria-hidden />
          </button>
          <span data-testid="nav-tip-count">{index + 1}/{tips.length}</span>
          <button type="button" onClick={() => step(1)} aria-label="Next tip" data-testid="nav-tip-next" className="flex size-5 items-center justify-center rounded hover:bg-white/15 hover:text-white">
            <ChevronRight className="size-3.5" aria-hidden />
          </button>
        </span>
      )}
    </div>
    <div className="grid">
      {tips.map((tip, i) => (
        <div key={tip.key} data-testid={`nav-tip-slide-${tip.key}`} aria-hidden={i !== index} className={`col-start-1 row-start-1 ${i === index ? "" : "invisible"}`}>
          <p className="mt-1.5 mb-2.5 text-xs leading-snug [@media(max-height:1000px)]:mt-1 [@media(max-height:1000px)]:mb-1.5">{tip.body}</p>
          <button
            type="button"
            onClick={tip.onTry}
            data-testid={tip.testId}
            tabIndex={i === index ? 0 : -1}
            className="w-full rounded-md border border-white bg-white py-1 text-xs font-semibold text-[#1f2a37] hover:bg-white/85 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/60"
          >
            Try it
          </button>
        </div>
      ))}
    </div>
  </div>
  );
}

const NavCollapseToggle = ({ isMini, onClick }: { isMini: boolean; onClick: () => void }) => {
  const label = isMini ? "Expand navigation" : "Collapse navigation";
  const Icon = isMini ? PanelLeftOpen : PanelLeftClose;
  return (
    <div className={`hidden lg:flex shrink-0 py-1 transition-opacity ${isMini ? "justify-center border-t border-white/10" : "justify-end pr-2"}`}>
      <button
        type="button"
        onClick={onClick}
        aria-label={label}
        data-testid="nav-collapse-toggle"
        className="group/toggle relative flex size-8 items-center justify-center rounded-full text-white/70 hover:bg-white/15 hover:text-white focus-visible:bg-white/15 focus-visible:outline-none"
      >
        <Icon className="size-4" aria-hidden />
        <span
          role="tooltip"
          className="pointer-events-none absolute left-[calc(100%+8px)] top-1/2 z-50 -translate-y-1/2 whitespace-nowrap rounded-md bg-[#1f1f23] px-3 py-1.5 text-[13px] text-white opacity-0 group-hover/toggle:opacity-100 group-focus-visible/toggle:opacity-100"
        >
          {label}
        </span>
      </button>
    </div>
  );
}

const MenuSkeleton = ({ isMini }: { isMini: boolean }) => (
  <ul className={`py-2 mt-2 space-y-0.5 ${!isMini ? "pl-2.5" : ""}`} aria-hidden="true">
    {SKELETON_COUNTS.map((width, i) => (
      <li key={i} className={`flex items-center gap-2.5 px-2 py-2 rounded ${isMini ? "justify-center" : ""}`}>
        <div className="animate-pulse rounded bg-white/20 shrink-0" style={{ width: 18, height: 18 }} />
        {!isMini && <div className="animate-pulse rounded bg-white/20 h-3.5" style={{ width: `${width * 18}px` }} />}
      </li>
    ))}
  </ul>
);

export const SideNav = (props: { menuItems: MenuItem[]; menuLoading: boolean; menuError: boolean; onOpenSettings?: () => void }) => {
  const parent = useContext(NavPanelContext);
  const local = useNavPanelState();
  if (parent) return <SideNavContent {...props} />;
  return (
    <NavPanelContext.Provider value={local}>
      <SideNavContent {...props} />
    </NavPanelContext.Provider>
  );
}

const SideNavContent = ({ menuItems, menuLoading, menuError, onOpenSettings }: { menuItems: MenuItem[]; menuLoading: boolean; menuError: boolean; onOpenSettings?: () => void }) => {
  const { sideNavState, mobileNavOpen, data, toggleSideNav } = useLayout();
  const { collapsed, toggle: toggleSection } = useCollapsedSections(collapsedSectionsKey(data.username));
  const [jumpOpen, setJumpOpen] = useState(false);
  const { entries: whatsNewEntries, hasUnseen, openWhatsNew } = useWhatsNew();
  const [whatsNewUnseenAtLoad] = useState(hasUnseen);
  const { snoozed: tipSnoozed, snooze: snoozeTip } = useNavTipSnooze(navTipKey(data.username));
  // On mobile (drawer open), always treat as wide so labels are visible
  const isMini = sideNavState === "mini" && !mobileNavOpen;
  const mainItems = menuItems.filter((i) => i.type !== "settings");
  const settingsItems = onOpenSettings ? menuItems.filter((i) => i.type === "settings") : [];

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setJumpOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const visibleMain = mainItems.filter((i) => i.isVisible);
  const selectedSections = getSelectedSections(visibleMain);
  const hasPinned = !isMini && !visibleMain[0]?.section && !!visibleMain[1]?.section;
  const scrollbarBelowPinned = hasPinned ? "[&>[data-orientation=vertical]]:top-[62.5px]! [&>[data-orientation=vertical]]:h-auto! [@media(max-height:1000px)]:[&>[data-orientation=vertical]]:top-[58.5px]!" : "";

  return (
    <nav
      role="navigation"
      className="layout-side-nav group/nav"
      aria-label="Main navigation"
    >
      <ScrollArea className={`flex-1 min-h-0 [&>[data-radix-scroll-area-viewport]]:overscroll-contain ${scrollbarBelowPinned}`}>
        {/* Mobile/tablet-portrait search — hidden on md+ (lives in TopBar there) */}
        <div className="md:hidden border-b border-white/20">
          <SearchBar variant="stacked" />
        </div>
        {menuError ? (
          <p className="px-4 py-4 text-sm text-white/60">Navigation failed to load.</p>
        ) : menuLoading ? (
          <MenuSkeleton isMini={isMini} />
        ) : (
          <>
            <ul className={`pb-2 space-y-0.5 ${isMini ? "pt-3" : `pl-2.5 ${hasPinned ? "pt-0" : "pt-5"}`}`}>
              {[...mainItems, ...settingsItems].map((item) => {
                const index = visibleMain.indexOf(item);
                const previous = index > 0 ? visibleMain[index - 1] : undefined;
                const startsSection = item.isVisible && !!item.section && item.section !== previous?.section;
                const section = index >= 0 ? getSectionName(visibleMain, index) : null;
                const pinned = !isMini && index === 0 && !item.section && !!visibleMain[1]?.section;
                const locked = !!section && selectedSections.has(section);
                const expanded = isMini || !section || locked || !collapsed.has(section);
                return (
                  <Fragment key={item.id}>
                    {startsSection && (
                      <SectionHeading
                        label={item.section!}
                        isMini={isMini}
                        isFirst={index === 0}
                        afterUngrouped={!!previous && !previous.section}
                        afterPinned={index === 1 && !isMini && !previous?.section}
                        expanded={expanded}
                        locked={locked}
                        onToggle={() => { if (!locked) toggleSection(item.section!); }}
                      />
                    )}
                    {(item.startsNewGroup || item.type === "settings") && !startsSection && <GroupGap isMini={isMini} />}
                    {expanded && <MenuItemRow item={item} isMini={isMini} onOpenSettings={onOpenSettings} pinned={pinned} />}
                  </Fragment>
                );
              })}
            </ul>
            {mobileNavOpen && <RecentCompanies />}
          </>
        )}
      </ScrollArea>

      <JumpToPalette menuItems={menuItems} open={jumpOpen} onOpenChange={setJumpOpen} />
      <NavCollapseToggle isMini={isMini} onClick={toggleSideNav} />
      {!isMini && !menuLoading && !menuError && !tipSnoozed && <NavTip onTry={() => setJumpOpen(true)} onDismiss={snoozeTip} onTryWhatsNew={openWhatsNew} showWhatsNew={whatsNewEntries.length > 0} whatsNewFirst={whatsNewUnseenAtLoad} />}
    </nav>
  );
}
